// FidelityPanelDemo.jsx
// -----------------------------------------------------------------------------
// Dev-only demo page for FidelityPanelMockup.
//
// Mounted at /dev/fidelity-panel-mockup via router.jsx (env-gated; dev only).
//
// Renders a toggle bar (one button per fixture in mockFidelityStates.js) and
// a single FidelityPanelMockup below that switches its props as the operator
// clicks toggles. Re-record and Save & Submit events are captured to an
// event log card so the action wiring is observable without instrumenting
// devtools.
//
// Spec reference: docs/journal/PHASE_4D_VA_FIDELITY_GATE_DESIGN.md
// -----------------------------------------------------------------------------

import React, { useState, useCallback } from 'react';
import FidelityPanelMockup from './FidelityPanelMockup';
import mockFidelityStates from './mockFidelityStates';
import './fidelityPanel.css';

const STATE_KEYS = Object.keys(mockFidelityStates);

const FidelityPanelDemo = () => {
  const [activeKey, setActiveKey] = useState(STATE_KEYS[0]);
  const [events, setEvents] = useState([]);

  const active = mockFidelityStates[activeKey];

  const logEvent = useCallback((kind, payload) => {
    const ts = new Date().toLocaleTimeString();
    setEvents((prev) => [
      `[${ts}] ${kind} (state=${activeKey}) ${JSON.stringify(payload || {})}`,
      ...prev,
    ].slice(0, 25));
  }, [activeKey]);

  const handleReRecord = useCallback(() => {
    logEvent('Re-record', {});
  }, [logEvent]);

  const handleSaveSubmit = useCallback((payload) => {
    logEvent('Save & Submit', payload);
  }, [logEvent]);

  // Reset acknowledgement state by remounting the panel when state changes.
  // The component holds local state (acknowledging, vaReason) and we want a
  // clean panel each time the operator switches band.
  const panelKey = `${activeKey}-${events.length === 0 ? 0 : events[0]}`;

  return (
    <main className="aai-fp-demo">
      <h1 className="aai-fp-demo__title">VA Fidelity Gate — Inline Panel Mockup</h1>
      <p className="aai-fp-demo__subtitle">
        Inline fidelity-warning panel that surfaces between Stop and Save in the
        VA's RecordingStudio. Pure-presentational mockup — no server, no audio,
        no persistence. Toggle bands below to inspect each UX state. Spec lives
        at <code>docs/journal/PHASE_4D_VA_FIDELITY_GATE_DESIGN.md</code>. Tune
        copy in <code>FidelityPanelMockup.jsx</code> (BAND_META); tune palette
        in <code>fidelityPanel.css</code>.
      </p>

      <div className="aai-fp-demo__toggles" role="tablist" aria-label="Fidelity state">
        {STATE_KEYS.map((key) => {
          const isActive = key === activeKey;
          return (
            <button
              key={key}
              type="button"
              role="tab"
              aria-selected={isActive}
              className={`aai-fp-demo__toggle ${isActive ? 'aai-fp-demo__toggle--active' : ''}`}
              onClick={() => setActiveKey(key)}
            >
              {mockFidelityStates[key].label}
            </button>
          );
        })}
      </div>

      <FidelityPanelMockup
        key={panelKey}
        promptText={active.promptText}
        score={active.score}
        onReRecord={handleReRecord}
        onSaveSubmit={handleSaveSubmit}
      />

      <section className="aai-fp-demo__eventlog" aria-label="Event log">
        <p className="aai-fp-demo__eventlog-title">Event log (last 25)</p>
        {events.length === 0 && (
          <p className="aai-fp-demo__eventlog-entry">No events yet. Click Re-record or Save &amp; Submit on the panel above.</p>
        )}
        {events.map((e, i) => (
          <p key={i} className="aai-fp-demo__eventlog-entry">{e}</p>
        ))}
      </section>
    </main>
  );
};

export default FidelityPanelDemo;
